
export interface ChatMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
  mainUrl?: { obj: string; val: string };
  otherUrl?: { obj: string; val: string }[];
}
import { AddAdditionalAddOfferForm, AddIndividualOfferForm, AdditionalOfferForm, AddOfferForm, AddPartnerForm, AttributeResponse, DataTypeResponse, IndividualOfferData, IndividualOfferForm, ModifyRule, OfferForm, PartnerForm, RuleComposeResponse, RuleForm, ToggleData } from '@/shared/partnerschema';
import { apiClient, authClient, apiCodeClient, AuthUser, RefreshTokenResponse, apiRuleClient } from './api-client';
import { Code } from '@/shared/schema';


export interface PaginatedResponse<T> {
  items: T[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  }
}

export interface CreateUserDto {
  name: string;
  email: string;
  password: string;
  role: string;
}

export interface UpdateUserDto extends Partial<Omit<CreateUserDto, 'password'>> {
  password?: string;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

// Partner Types
export interface PartnerData {
  items: PartnerForm[];
  page_no: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface OfferData {
  items: OfferForm[];
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

export interface AdditionalOfferData {
  items: AdditionalOfferForm[];
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

export interface RuleData {
  items: RuleForm[];
  page: number;
  pageSize: number;
  total: number;
}


// ==================== BPRM AUTH SERVICES ====================

export const authService = {
  me: async (): Promise<AuthUser> => {
    const response = await authClient.get<AuthUser>('/auth/me');
    return response.data;
  },

  refresh: async (): Promise<RefreshTokenResponse> => {
    const response = await authClient.post<RefreshTokenResponse>('/auth/refresh');
    return response.data;
  },

  logout: async (): Promise<{ success: boolean; message: string }> => {
    const response = await authClient.post('/auth/logout');
    return response.data;
  },

  offerFormId: async (id: number, referenceFormId: number): Promise<{ success: boolean; message: string, data: any }> => {
    console.log("referenceFormId", referenceFormId)
    const response = await authClient.put(`/exchange-offers/${id}/reference-form`, { ReferenceFormId: referenceFormId });
    return response.data;
  },

  additionalOfferFormId: async (id: number, additionalOfferId: number, referenceFormId: number): Promise<{ success: boolean; message: string, data: any }> => {
    console.log("referenceFormId", referenceFormId)
    const response = await authClient.put(`/exchange-offers/${id}/additional-offers/${additionalOfferId}/reference-form`, { ReferenceFormId: referenceFormId });
    return response.data;
  },
};

// ==================== PARTNER SERVICES ====================
export const partnerService = {
  chatWithAI: async (
    message: string,
    sessionId?: string,
    microservice: string = "partner",
    page: number = 1
  ): Promise<{ assistantReply: string; sessionId: string; messages?: any[]; hasMore?: boolean; totalMessages?: number }> => {
    const payload: any = { microservice };
    if (message) payload.message = message;
    if (sessionId) payload.sessionId = sessionId;

    const response = await authClient.post(`/assistant/chat?page=${page}`, payload);
    return response.data;
  },

  getPartners: async (
    pageNumber: string,
    pageSize?: string,
    partnerName?: string,
    partnerType?: string,
    partnerSubType?: string
  ): Promise<ApiResponse<PartnerData>> => {
    const params = new URLSearchParams({
      page: pageNumber.toString(),
    });

    if (pageSize) {
      params.append("pageSize", pageSize);
    }
    if (partnerName && partnerName.trim()) {
      params.append("partnerName", partnerName);
    }
    if (partnerType && partnerType.trim()) {
      params.append("partnerType", partnerType);
    }
    if (partnerSubType && partnerSubType) {
      params.append("partnerSubType", partnerSubType);
    }

    const response = await apiClient.get(`/partnerforms?${params.toString()}&includeInactive=false`);
    return response.data;
  },

  addPartner: async (data: AddPartnerForm): Promise<ApiResponse<AddPartnerForm>> => {
    const response = await apiClient.post<ApiResponse<AddPartnerForm>>(`/partnerforms`, data);
    return response.data;
  },

  editPartner: async (data: AddPartnerForm): Promise<ApiResponse<AddPartnerForm>> => {
    const response = await apiClient.put<ApiResponse<AddPartnerForm>>(`/partnerforms/${data.ID}`, data);
    return response.data;
  },

  deletePartner: async (id: number | undefined): Promise<ApiResponse<null>> => {
    const response = await apiClient.delete<ApiResponse<null>>(`/partnerforms/${id}`);
    return response.data;
  },

  searchPartners: async (
    name: string = "",
    page: number = 1,
    pageSize: number = 20,
    sort: string = "Lex"
  ): Promise<any> => {
    const params = new URLSearchParams({
      type: "Partner",
      name: name || "",
      sort: sort || "Lex",
      page: String(page),
      pageSize: String(pageSize),
    });

    const response = await apiClient.get(`/search?${params.toString()}`);
    return response.data;
  },
};

export interface PartnerSearchItem {
  id: number | string;
  partnerName: string;
  partnerType?: string;
  partnerSubType?: string;
  description?: string;
  raw?: any;
}

export function parsePartnerSearchResults(response: any): {
  items: PartnerSearchItem[];
  total?: number;
  hasMore: boolean;
} {
  if (!response) {
    return { items: [], hasMore: false };
  }

  let rawList: any[] = [];
  let total: number | undefined;

  const payload = response.data !== undefined ? response.data : response;

  if (Array.isArray(payload)) {
    rawList = payload;
  } else if (payload && typeof payload === "object") {
    if (Array.isArray(payload.items)) {
      rawList = payload.items;
      total = payload.total ?? payload.totalCount ?? payload.total_count ?? payload.totalRecords;
    } else if (Array.isArray(payload.data)) {
      rawList = payload.data;
      total = payload.total ?? payload.totalCount;
    } else if (Array.isArray(payload.partners)) {
      rawList = payload.partners;
      total = payload.total;
    } else if (Array.isArray(payload.results)) {
      rawList = payload.results;
      total = payload.total ?? payload.count;
    }
  }

  const items: PartnerSearchItem[] = rawList.map((item: any, index: number) => {
    if (typeof item === "string") {
      return { id: item, partnerName: item };
    }
    const id = item.ID ?? item.Id ?? item.id ?? item.PartnerId ?? item.partnerId ?? index;
    const partnerName = item.PartnerName ?? item.partnerName ?? item.Name ?? item.name ?? item.label ?? `Partner ${id}`;
    return {
      id,
      partnerName,
      partnerType: item.PartnerType ?? item.partnerType,
      partnerSubType: item.PartnerSubType ?? item.partnerSubType ?? item.PartnerSubName ?? item.partnerSubName,
      description: item.Description ?? item.description,
      raw: item,
    };
  });

  const hasMore = total !== undefined ? items.length > 0 && items.length >= 20 : rawList.length >= 20;

  return { items, total, hasMore };
}


// ==================== OFFER SERVICES ====================
export const offerService = {
  getOffers: async (
    page: number,
    pageSize: number
  ): Promise<ApiResponse<OfferData>> => {
    const response = await apiClient.get(`/exchange-offers?page=${page}&pageSize=${pageSize}`)
    return response.data;
  },

  addOffer: async (data: AddOfferForm): Promise<ApiResponse<OfferForm>> => {
    const response = await apiClient.post<ApiResponse<OfferForm>>(`/exchange-offers`, data)
    return response.data;
  },

  editOffer: async (data: any): Promise<ApiResponse<OfferForm>> => {
    const payload = {
      ExPartnerId: data.ExPartnerId,
      ExchangeOfferName: data.ExchangeOfferName,
      OfferDetails: data.OfferDetails
    };
    const response = await apiClient.put<ApiResponse<OfferForm>>(`/exchange-offers/${data?.Id}`, payload)
    return response.data;
  },

  getIndividualOffers: async (
    page: number,
    pageSize: number
  ): Promise<ApiResponse<IndividualOfferData>> => {
    const response = await apiClient.get(`/individual-offer?page=${page}&pageSize=${pageSize}`)
    return response.data;
  },

  addIndivisualOffer: async (data: AddIndividualOfferForm): Promise<ApiResponse<IndividualOfferForm>> => {
    const response = await apiClient.post<ApiResponse<IndividualOfferForm>>(`/individual-offers`, data)
    return response.data;
  },

  editIndivisualOffer: async (data: any): Promise<ApiResponse<IndividualOfferForm>> => {
    const payload = {
      OfferType: data.OfferType,
      ExchangeOfferName: data.ExchangeOfferName,
      OfferDetails: data.OfferDetails,
      ValidFrom: data.ValidFrom,
      ValidTo: data.ValidTo
    };
    const response = await apiClient.put<ApiResponse<IndividualOfferForm>>(`/individual-offer/${data?.Id}`, payload)
    return response.data;
  },

  toggleOfferStatus: async (Id: number): Promise<ApiResponse<AddOfferForm>> => {
    const response = await apiClient.patch<ApiResponse<AddOfferForm>>(`/exchange-offers/${Id}/toggle-active`)
    return response.data;
  },

  // Additional Offer operations
  getAdditionalOffers: async (offerId: number, pageNumber: number, pageSize: number): Promise<ApiResponse<AdditionalOfferData>> => {
    const response = await apiClient.get<ApiResponse<AdditionalOfferData>>(`/exchange-offers/${offerId}/additional-offers?page=${pageNumber}&pageSize=${pageSize}`)
    return response.data;
  },

  addAdditionalOffer: async (offerId: number, data: AddAdditionalAddOfferForm): Promise<ApiResponse<AdditionalOfferForm>> => {
    const response = await apiClient.post<ApiResponse<AdditionalOfferForm>>(`exchange-offers/${offerId}/additional-offers`, data)
    return response.data;
  },

  editAdditionalOffer: async (offerId: number, additionalOfferId: number, data: AddAdditionalAddOfferForm): Promise<ApiResponse<AddAdditionalAddOfferForm>> => {
    const response = await apiClient.put<ApiResponse<AddAdditionalAddOfferForm>>(`/exchange-offers/${offerId}/additional-offers/${additionalOfferId}`, data)
    return response.data;
  },

  // Rule operations
  getRules: async (page = 1, pageSize: number| undefined, offerId?: number | null): Promise<RuleData> => {
    const response = await authClient.get<RuleData>(
      `/rules-service/api/partner-rules-offer?offerId=${offerId}&page=${page}`
    );
    return response.data;
  },

  getRuleCompose: async (ruleId: string): Promise<RuleComposeResponse> => {
    const response = await authClient.get<RuleComposeResponse>(
      `/rules-service/api/rules/${ruleId}/compose`
    );
    return response.data;
  },

  // Attributes & DataTypes
  getAttributes: async (parentCode: string): Promise<AttributeResponse> => {
    const response = await authClient.get<AttributeResponse>(
      `/datatype-service/api/datatypes/${parentCode}/attributes`
    );

    return response.data;
  },

  getDataTypes: async (
  ): Promise<DataTypeResponse> => {
    const response = await authClient.get<DataTypeResponse>(
      `/datatype-service/api/datatypes?order=newest`
    );
    return response.data;
  },

  createRuleValue: async (data: AddOfferForm): Promise<ModifyRule> => {
    const response = await authClient.put<ModifyRule>(`/rules-service/api/partner-rules-offer/edit`, data)
    return response.data;
  },

  updateRuleValue: async (objverid: string, data: AddOfferForm): Promise<ModifyRule> => {
    const response = await authClient.patch<ModifyRule>(`/rules-service/api/partner-rules-offer/${objverid}/edit`, data)
    return response.data;
  },

  toggleRuleValueStatus: async (objverid: string, offId: string): Promise<ApiResponse<ToggleData>> => {
    const response = await authClient.patch<ApiResponse<ToggleData>>(`/rules-service/api/partner-rules-offer/${objverid}/toggle-approved?offerId=${offId}`)
    return response.data;
  },

  getCodeFilterApi: async (
        name?: number,
        types?: string,
        parentType?: string,
    ): Promise<Code[]> => {
        const params = new URLSearchParams();

        if (name !== undefined) params.append("name", String(name));
        if (types) params.append("type", types);
        if (parentType) params.append("parentType", parentType);

        params.append("includeDeleted", "false");

        const response = await apiCodeClient.get<Code[]>(
            `/catalog/filter?${params.toString()}`
        );

        return response.data;
    }

};